import type { Infer, Struct } from 'superstruct';
import {
	array,
	boolean,
	dynamic,
	never,
	nullable,
	number,
	object,
	optional,
	partial,
	size,
	string,
} from 'superstruct';

import { ViewMediaDto } from '@adflow/shared/data-media';
import {
	emptyStringIsNull,
	parsableInteger,
	stringDate,
	userString,
	withMessage,
} from '@adflow/shared/util-superstruct';

import { ViewGMBLocationDto } from './gmb-location.dto';

export const GMB_STANDARD_CTA_TYPE_CTA_URL_NOT_NEEDED = ['CALL'];

export const ViewGMBTemplateDto = object({
	id: number(),
	name: string(),

	title: optional(nullable(string())),
	summary: optional(nullable(string())),
	startDate: optional(nullable(stringDate())),
	endDate: optional(nullable(stringDate())),
	postType: optional(nullable(string())),

	languageCode: optional(nullable(string())),
	ctaType: optional(nullable(string())),
	ctaUrl: optional(nullable(string())),
	redeemOnlineUrl: optional(nullable(string())),
	couponCode: optional(nullable(string())),
	termsConditions: optional(nullable(string())),
	archived: boolean(),
	imported: boolean(),

	image: optional(nullable(ViewMediaDto)),

	createdAt: stringDate(),
	updatedAt: stringDate(),
	scheduledPublishAt: optional(nullable(stringDate())),

	createdById: number(),

	locations: optional(array(ViewGMBLocationDto)),

	posts: optional(
		array(
			object({
				id: number(),
				googleId: string(),
				createdAt: stringDate(),
			})
		)
	),
});

export type ViewGMBTemplateDto = Infer<typeof ViewGMBTemplateDto>;

export function postTypeValidation<T, V>(
	defaultStruct: Struct<T, null>,
	validatedStruct: Struct<V, null>,
	postTypes: ('STANDARD' | 'OFFER' | 'EVENT')[]
): Struct<T | V, null> {
	return dynamic<T | V>((_value, context) => {
		const contextValues = context.branch.at(-2);
		if (
			postTypes.includes(contextValues.postType) &&
			!GMB_STANDARD_CTA_TYPE_CTA_URL_NOT_NEEDED.includes(contextValues.ctaType)
		) {
			return validatedStruct;
		}

		return defaultStruct;
	});
}

export const AddGMBTemplateDto = object({
	id: optional(parsableInteger()),
	name: userString('validations:Required'),
	archived: optional(boolean()),

	postType: string(),
	languageCode: string(),
	ctaType: string(),
	ctaUrl: postTypeValidation(
		optional(string()),
		userString('validations:Required'),
		['STANDARD']
	),
	redeemOnlineUrl: optional(nullable(emptyStringIsNull(string()))),
	couponCode: optional(nullable(emptyStringIsNull(string()))),
	termsConditions: optional(nullable(emptyStringIsNull(string()))),

	title: withMessage(size(string(), 0, 58), 'validations:Max Length'),
	summary: string(),
	startDate: dynamic((value, context) => {
		const contextValues = context.branch.at(-2);
		if (contextValues.postType === 'STANDARD') {
			return optional(nullable(emptyStringIsNull(stringDate())));
		}

		if (typeof value === 'string') {
			const endDate = contextValues.endDate;
			const isGreater =
				!value || !endDate ? true : new Date(value) >= new Date(endDate);
			if (isGreater) {
				return withMessage(
					never(),
					'validations:DateTime startDateTimeBeforeEnd'
				);
			}
		}
		return withMessage(stringDate(), 'validations:Required');
	}),
	endDate: optional(nullable(emptyStringIsNull(stringDate()))),

	image: optional(nullable(ViewMediaDto)),
	locations: optional(array(ViewGMBLocationDto)),
});

export type AddGMBTemplateDto = Infer<typeof AddGMBTemplateDto>;
export const EditGMBTemplateDto = partial(AddGMBTemplateDto);
export type EditGMBTemplateDto = Infer<typeof EditGMBTemplateDto>;

export const ScheduleGMBTemplateDto = object({
	scheduledPublishAt: stringDate(),
});

export type ScheduleGMBTemplateDto = Infer<typeof ScheduleGMBTemplateDto>;
